import { useEffect, useRef, useState } from "react";
import api, { errorMessage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Building2, Loader2, Save, Upload, Trash2, Image as ImageIcon, Database, Sparkles, MessageCircle, Lock } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/ui/page-header";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const logoUrl = (p) => {
    if (!p) return "";
    if (p.startsWith("/api/")) return `${BACKEND}${p}`;
    if (p.startsWith("/uploads/")) return `${BACKEND}${p}`;
    return p;
};

// v1.8.29 — di luar komponen supaya input tidak remount/kehilangan fokus tiap ketik
const Section = ({ icon: Icon, title, description, children, testid }) => (
    <section
        className="rounded-xl bg-white p-5 md:p-6"
        style={{ border: "1px solid var(--border)" }}
        data-testid={testid}
    >
        <div className="flex items-start gap-3 mb-5">
            <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: "var(--apple-blue-soft)", color: "var(--apple-blue)" }}
            >
                <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
                <h2 className="font-semibold text-base" style={{ color: "var(--text-primary)" }}>{title}</h2>
                {description && <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{description}</p>}
            </div>
        </div>
        {children}
    </section>
);

export default function PengaturanPage() {
    const nav = useNavigate();
    const { user } = useAuth();
    const [form, setForm] = useState({
        nama_perusahaan: "", alamat: "", no_telp: "", email: "", logo_url: "",
        wa_template: "",
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savingWa, setSavingWa] = useState(false);
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get("/pengaturan");
            setForm({
                nama_perusahaan: data.nama_perusahaan || "",
                alamat: data.alamat || "",
                no_telp: data.no_telp || "",
                email: data.email || "",
                logo_url: data.logo_url || "",
                wa_template: data.wa_template || "",
            });
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!form.nama_perusahaan.trim()) return toast.error("Nama perusahaan wajib diisi");
        setSaving(true);
        try {
            await api.put("/pengaturan", {
                nama_perusahaan: form.nama_perusahaan,
                alamat: form.alamat, no_telp: form.no_telp, email: form.email,
                logo_url: form.logo_url,
            });
            toast.success("Pengaturan perusahaan disimpan");
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setSaving(false); }
    };

    const DEFAULT_WA_TEMPLATE = (
        "Halo {vendor_nama},\n" +
        "Berikut nota {jenis} - {nota_kode}.\n" +
        "Link: {share_url}\n" +
        "\n" +
        "Terima kasih,\n" +
        "{perusahaan}"
    );

    const saveWaTemplate = async () => {
        setSavingWa(true);
        try {
            await api.put("/pengaturan", {
                nama_perusahaan: form.nama_perusahaan,
                alamat: form.alamat, no_telp: form.no_telp, email: form.email,
                logo_url: form.logo_url,
                wa_template: form.wa_template || "",
            });
            toast.success("Template WhatsApp disimpan");
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setSavingWa(false); }
    };

    const resetWaTemplate = () => {
        setForm((p) => ({ ...p, wa_template: DEFAULT_WA_TEMPLATE }));
    };

    const waPreview = (() => {
        const tpl = form.wa_template || DEFAULT_WA_TEMPLATE;
        return tpl
            .replaceAll("{vendor_nama}", "Pak Budi")
            .replaceAll("{jenis}", "Bon Kain")
            .replaceAll("{nota_kode}", "BK-2026-0042")
            .replaceAll("{share_url}", "https://bvg.app/api/share/abc123XYZ")
            .replaceAll("{perusahaan}", form.nama_perusahaan || "CV. Meranggi Jaya Garment");
    })();

    const uploadLogo = async (f) => {
        if (!f) return;
        if (f.size > 2 * 1024 * 1024) return toast.error("Logo maksimal 2MB");
        if (!/^image\/(jpeg|jpg|png|webp|svg\+xml)$/i.test(f.type)) {
            return toast.error("Format tidak didukung. Pakai jpg/png/webp/svg.");
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("logo", f);
            const r = await api.post("/pengaturan/logo", fd,
                { headers: { "Content-Type": "multipart/form-data" } });
            setForm((prev) => ({ ...prev, logo_url: r.data.logo_url }));
            toast.success(`Logo diupload (${r.data.size_kb} KB)`);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
    };

    const hapusLogo = async () => {
        if (!window.confirm("Hapus logo? Placeholder akan dipakai di PDF & sidebar.")) return;
        try {
            await api.delete("/pengaturan/logo");
            setForm((p) => ({ ...p, logo_url: "" }));
            toast.success("Logo dihapus");
        } catch (e) { toast.error(errorMessage(e)); }
    };

    if (loading) return (
        <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--apple-blue)" }} />
        </div>
    );

    return (
        <div data-testid="pengaturan-page">
            <PageHeader
                title="Pengaturan"
                description="Data perusahaan, logo, shortcut AI config & backup."
            />

            <div className="space-y-4 md:space-y-5 max-w-3xl">
                {/* Logo */}
                <Section icon={ImageIcon} title="Logo Perusahaan" description="Tampil di sidebar, login, dan kop PDF." testid="section-logo">
                    <div className="flex items-center gap-5 flex-wrap">
                        <div
                            className="w-24 h-24 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0"
                            style={{ background: "var(--bg-subtle)", border: "1px solid var(--border-soft)" }}
                            data-testid="logo-preview"
                        >
                            {form.logo_url ? (
                                <img src={logoUrl(form.logo_url)} alt="logo"
                                     className="w-full h-full object-contain"
                                     onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            ) : (
                                <span className="text-[10px] text-center px-2" style={{ color: "var(--text-muted)" }}>
                                    Belum ada logo
                                </span>
                            )}
                        </div>
                        <div className="flex-1 min-w-[200px]">
                            <div className="flex gap-2 mb-2 flex-wrap">
                                <Button
                                    type="button"
                                    onClick={() => inputRef.current?.click()}
                                    disabled={uploading}
                                    data-testid="btn-upload-logo"
                                    className="rounded-lg gap-1.5 text-white"
                                    style={{ background: "var(--apple-blue)" }}
                                >
                                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                    {form.logo_url ? "Ganti Logo" : "Upload Logo Baru"}
                                </Button>
                                {form.logo_url && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={hapusLogo}
                                        data-testid="btn-delete-logo"
                                        className="rounded-lg gap-1.5"
                                        style={{ borderColor: "var(--apple-red)", color: "var(--apple-red)" }}
                                    >
                                        <Trash2 className="w-4 h-4" /> Hapus
                                    </Button>
                                )}
                                <input
                                    ref={inputRef}
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp,image/svg+xml"
                                    hidden
                                    onChange={(e) => uploadLogo(e.target.files?.[0])}
                                    data-testid="input-logo-file"
                                />
                            </div>
                            <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                                Format: jpg/png/webp/svg · Max 2MB · Crop otomatis 2cm×2cm di PDF.
                            </p>
                        </div>
                    </div>
                </Section>

                {/* Info Perusahaan */}
                <Section icon={Building2} title="Info Perusahaan" description="Dipakai pada kop invoice, laporan, dan dokumen resmi." testid="section-info">
                    <div className="space-y-4">
                        <div>
                            <Label>Nama Perusahaan <span style={{ color: "var(--apple-red)" }}>*</span></Label>
                            <Input
                                value={form.nama_perusahaan}
                                onChange={(e) => setForm({ ...form, nama_perusahaan: e.target.value })}
                                data-testid="pengaturan-nama"
                                className="mt-1.5 rounded-lg"
                            />
                        </div>
                        <div>
                            <Label>Alamat</Label>
                            <Textarea
                                value={form.alamat}
                                onChange={(e) => setForm({ ...form, alamat: e.target.value })}
                                data-testid="pengaturan-alamat"
                                className="mt-1.5 rounded-lg"
                                rows={3}
                            />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label>No. Telepon</Label>
                                <Input
                                    value={form.no_telp}
                                    onChange={(e) => setForm({ ...form, no_telp: e.target.value })}
                                    data-testid="pengaturan-telp"
                                    className="mt-1.5 rounded-lg"
                                    placeholder="+62 22 ..."
                                />
                            </div>
                            <div>
                                <Label>Email</Label>
                                <Input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    data-testid="pengaturan-email"
                                    className="mt-1.5 rounded-lg"
                                    placeholder="admin@example.com"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end pt-1">
                            <Button
                                onClick={save}
                                disabled={saving}
                                data-testid="pengaturan-save-button"
                                className="rounded-lg gap-2 text-white"
                                style={{ background: "var(--apple-blue)" }}
                            >
                                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Menyimpan...</> : <><Save className="w-4 h-4" /> Simpan</>}
                            </Button>
                        </div>
                    </div>
                </Section>

                {/* Shortcut Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button
                        onClick={() => nav("/konfigurasi-ai")}
                        className="rounded-xl bg-white p-5 text-left hover:bg-slate-50 transition-colors"
                        style={{ border: "1px solid var(--border)" }}
                        data-testid="shortcut-ai"
                    >
                        <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                            style={{ background: "#F3EBFF", color: "#7E3BFF" }}
                        >
                            <Sparkles className="w-5 h-5" />
                        </div>
                        <div className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>Konfigurasi AI</div>
                        <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>Atur API key OpenAI, model, dan rate limit.</div>
                    </button>
                    <button
                        onClick={() => nav("/backup")}
                        className="rounded-xl bg-white p-5 text-left hover:bg-slate-50 transition-colors"
                        style={{ border: "1px solid var(--border)" }}
                        data-testid="shortcut-backup"
                    >
                        <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                            style={{ background: "#E8F7EE", color: "#1DA359" }}
                        >
                            <Database className="w-5 h-5" />
                        </div>
                        <div className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>Backup Data</div>
                        <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>Backup harian, retention, dan unduh snapshot.</div>
                    </button>
                </div>

                {/* Template Pesan WhatsApp — OWNER ONLY */}
                <Section
                    icon={MessageCircle}
                    title="Template Pesan WhatsApp"
                    description="Pesan default saat kirim nota ke vendor via tombol Kirim WhatsApp. Placeholder didukung: {vendor_nama}, {jenis}, {nota_kode}, {share_url}, {perusahaan}."
                    testid="section-wa-template"
                >
                    {/* v1.8.12 — WA Template dibuka ke semua authenticated user (monika juga) */}
                    <div className="space-y-4">
                        <div>
                            <Label>Template Pesan</Label>
                            <Textarea
                                value={form.wa_template}
                                onChange={(e) => setForm({ ...form, wa_template: e.target.value })}
                                placeholder={DEFAULT_WA_TEMPLATE}
                                data-testid="wa-template-input"
                                className="mt-1.5 rounded-lg font-mono text-xs"
                                rows={7}
                            />
                            <p className="text-[11px] mt-1.5" style={{ color: "var(--text-muted)" }}>
                                Kosongkan untuk pakai template default.
                            </p>
                        </div>
                        <div>
                            <Label>Preview</Label>
                            <div
                                className="mt-1.5 rounded-lg p-3 text-xs whitespace-pre-wrap font-mono"
                                style={{
                                    background: "rgba(37, 211, 102, 0.08)",
                                    border: "1px solid rgba(37, 211, 102, 0.25)",
                                    color: "var(--text-primary)",
                                }}
                                data-testid="wa-template-preview"
                            >
                                {waPreview}
                            </div>
                            <p className="text-[11px] mt-1.5" style={{ color: "var(--text-muted)" }}>
                                Contoh pesan dengan data dummy. Link <code>{"{share_url}"}</code> akan dibuat otomatis saat tombol diklik.
                            </p>
                        </div>
                        {(
                            <div className="flex justify-end gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={resetWaTemplate}
                                    data-testid="wa-template-reset"
                                    className="rounded-lg gap-1.5"
                                >
                                    Pakai Default
                                </Button>
                                <Button
                                    onClick={saveWaTemplate}
                                    disabled={savingWa}
                                    data-testid="wa-template-save"
                                    className="rounded-lg gap-2 text-white"
                                    style={{ background: "#25D366" }}
                                >
                                    {savingWa ? <><Loader2 className="w-4 h-4 animate-spin" /> Menyimpan…</> : <><Save className="w-4 h-4" /> Simpan Template</>}
                                </Button>
                            </div>
                        )}
                    </div>
                </Section>
            </div>
        </div>
    );
}
